import api from './api'
import type { AxiosResponse } from 'axios'
import type { Workspace, TeamMember, WorkspaceBranding, WorkspaceDefaults, Redirect } from '@/types'
import { uploadWorkspaceLogo } from './media/branding'

/** This browser's time zone ('Europe/Berlin'), or undefined when it can't tell. */
export function browserTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined
  } catch {
    return undefined
  }
}

export type RedirectFields = Partial<Pick<Redirect, 'fromPath' | 'toUrl' | 'statusCode' | 'isActive' | 'forwardQueryString'>>
export interface RedirectUpdate { id: number; fields: RedirectFields }
export interface RedirectBatchResult { success: boolean; count: number; ids: number[] }

export type WorkspaceType =
  // Legacy types (backward compatibility)
  | 'ecommerce' | 'product-seller' | 'digital-creator' | 'course-seller' | 'coach-consultant'
  | 'freelancer' | 'agency' | 'event-organizer' | 'nonprofit' | 'content-creator' | 'influencer'
  | 'membership' | 'subscription' | 'bookings' | 'saas' | 'individual' | 'other'
  // Services
  | 'beauty-salon' | 'aesthetic-clinic' | 'pet-services' | 'home-services'
  | 'technical-services' | 'personal-trainer' | 'auto-detailing' | 'other-services'
  // Products
  | 'bakery-sweets' | 'homemade-food' | 'handmade-crafts' | 'fashion' | 'other-products'
  // Professional
  | 'other-professional'
  // Non-profit
  | 'fundraiser'

export interface CreateWorkspaceData {
  name: string
  /** The creator's browser time zone; the server keeps it only when it is a real zone. */
  timezone?: string
  workspaceType?: WorkspaceType
  /** Site-level theme — see Workspace.themeKey */
  themeKey?: string | null
}

export interface UpdateWorkspaceData {
  name?: string
  language?: string
  /** Business time zone picked in Team settings. */
  timezone?: string
  branding?: Partial<WorkspaceBranding>
  /** Site-level theme — see Workspace.themeKey */
  themeKey?: string | null
  /** When applying a kit (themeKey), keep the current brand instead of adopting the kit's palette. */
  keepBrand?: boolean
}

export interface CustomerOrderAggregate {
  total: number | string | null  // SUM(total) from Sequelize - may be string or number
}

export interface Lead {
  id: number
  workspaceId: number
  pageId: number
  formSettingsId: number
  email: string
  name: string | null
  phone: string | null
  data: Record<string, unknown> | null
  source: string
  page: { id: number; name: string } | null
  formSettings: { id: number; name: string | null } | null
  createdAt: string
  updatedAt: string
}

export interface LeadsResponse {
  leads: Lead[]
  total: number
  page: number
  totalPages: number
}

export interface Customer {
  contactId?: number | null
  crm?: {
    owner: import('./crm.service').CrmMember | null
    lead: { id: number; marketingOwnerId: number | null } | null
    salesLead?: { id: number; status: string; ownerId: number | null; openedAt: string } | null
    needsReview: boolean
    /** Journey stage from the shared Contact. */
    stage?: import('./crm.service').PersonStage | null
    /** The first time the person answered. Empty means they are still in the pool. */
    answeredAt?: string | null
  } | null
  id: number
  name: string
  email: string
  addressLine1?: string | null
  addressLine2?: string | null
  addressCity?: string | null
  addressState?: string | null
  addressCountry?: string | null
  addressPostalCode?: string | null
  connectedAccountId?: string | null
  workspaceId: number
  pageId?: number | null
  optIn: boolean
  /** Soft-archive timestamp — null/absent means the customer is active. */
  archivedAt?: string | null
  orders?: CustomerOrderAggregate[]  // Sequelize include with SUM(total)
  createdAt: string
  updatedAt: string
}

/** ?status filter for the customers list. Default 'active'. */
export type CustomerListStatus = 'active' | 'archived' | 'all'

/** Whitelisted fields the customer edit form can PATCH. */
export interface UpdateCustomerData {
  name: string
  email: string
  addressLine1?: string | null
  addressLine2?: string | null
  addressCity?: string | null
  addressState?: string | null
  addressCountry?: string | null
  addressPostalCode?: string | null
  optIn?: boolean
}

/** Paid-only lifetime spend stats (all money in integer cents). */
export interface CustomerStats {
  totalSpentCents: number
  orderCount: number
  largestOrderCents: number
  firstOrderAt: string | null
  latestOrderAt: string | null
}

/**
 * One normalized order in the merged (v1 + catalog) customer timeline.
 * `totalCents` is always integer cents regardless of source. `matchedBy`
 * is 'email' for guest checkouts folded in by email match.
 */
export interface CustomerHistoryRow {
  rowKey: string
  id: number
  source: 'v1' | 'catalog'
  matchedBy: 'customer' | 'email'
  totalCents: number
  currency: string
  status: string | null
  createdAt: string | null
  detailPath: string | null
  receiptUrl: string | null
  itemSummary: string | null
}

export interface CustomerDetail {
  customer: Customer
  stats: CustomerStats
  orders: {
    rows: CustomerHistoryRow[]
    total: number
    page: number
    limit: number
    totalPages: number
  }
}

/**
 * One row of a contact's sequence-trigger activity (Slice 4, moment 6): a moment
 * we evaluated and whether it started a sequence. `reason` is a machine code the
 * UI maps to plain English; `sequenceName` is resolved server-side.
 */
export interface CustomerTriggerActivityRow {
  id: number
  sequenceId: number
  sequenceName: string | null
  moment: string
  entered: boolean
  reason: string
  detail: Record<string, unknown> | null
  createdAt: string
}

export interface Order {
  id: string
  customer: number | null
  customerId: string | null
  processorOrderId: string | null
  workspaceId: number
  pageId: number | null
  pageType: string | null
  subscription: Record<string, unknown> | null
  orderInfo: Record<string, unknown>
  total: number
  processor: Record<string, unknown> | null
  method: string | null
  receiptUrl: string | null
  status: string
  country: string
  currency: string
  date: string | null
  createdAt: string
  updatedAt: string
  // Included association
  customerRecord?: { name: string } | null
}

export interface Subscription {
  id: string
  customerId: string | null
  connectedAccountId: string | null
  workspaceId: number
  pageId: number | null
  interval: string | null
  userAmount: number | null
  userTip: number | null
  userFee: number | null
  userTotalPaid: number | null
  isProcessingFeeEnabled: boolean | null
  totalPaid: number | null
  tip: number | null
  fee: number | null
  description: string | null
  status: string | null
  glCode: string | null
  date: string | null
  createdAt: string
  updatedAt: string
  // Included association
  customer?: { name: string } | null
}

export interface Transaction {
  id: string
  amount: number
  status: string
  createdAt: string
}

export interface WorkspaceTier {
  id: string
  name: string
  plan: string
  status: string
}

export const workspaceService = {
  /**
   * List all workspaces for current user
   * GET /workspaces
   */
  getAll(): Promise<AxiosResponse<Workspace[]>> {
    return api.get<Workspace[]>('/private/workspaces')
  },

  /**
   * Get a specific workspace
   * GET /workspaces/:id
   */
  get(id: string): Promise<AxiosResponse<Workspace>> {
    return api.get<Workspace>(`/private/workspaces/${id}`)
  },

  /**
   * Create a new workspace
   * POST /workspaces
   */
  create(data: CreateWorkspaceData): Promise<AxiosResponse<Workspace>> {
    return api.post<Workspace>('/private/workspaces', data)
  },

  /**
   * Update a workspace
   * PATCH /workspaces/:id
   */
  update(id: string, data: UpdateWorkspaceData): Promise<AxiosResponse<Workspace>> {
    return api.patch<Workspace>(`/private/workspaces/${id}`, data)
  },

  /**
   * A workspace still on the default time zone takes this browser's zone, once
   * (owners and admins only). Answers with the workspace's current values.
   * POST /workspaces/:id/time-zone/adopt
   */
  adoptTimeZone(id: string, timezone: string): Promise<AxiosResponse<Pick<Workspace, 'timezone' | 'timezoneSetBy'>>> {
    return api.post<Pick<Workspace, 'timezone' | 'timezoneSetBy'>>(`/private/workspaces/${id}/time-zone/adopt`, { timezone })
  },

  /**
   * Restore the brand snapshotted before a kit was applied (undo).
   * POST /workspaces/:id/restore-brand
   */
  restoreBrand(id: string): Promise<AxiosResponse<{ restored: boolean }>> {
    return api.post(`/private/workspaces/${id}/restore-brand`)
  },

  /**
   * Get workspace members
   * GET /workspaces/:id/members
   */
  getMembers(id: string): Promise<AxiosResponse<TeamMember[]>> {
    return api.get<TeamMember[]>(`/private/workspaces/${id}/members`)
  },

  /**
   * Get workspace customers
   * GET /workspaces/:id/customers?status=active|archived|all
   */
  getCustomers(id: string, status: CustomerListStatus = 'active'): Promise<AxiosResponse<Customer[]>> {
    return api.get<Customer[]>(`/private/workspaces/${id}/customers`, { params: { status } })
  },

  /**
   * Get a single customer with paid-only spend stats and a paginated,
   * merged (v1 + catalog) order timeline.
   * GET /workspaces/:id/customers/:customerId?page&limit
   */
  getCustomer(
    workspaceId: string,
    customerId: number | string,
    opts: { page?: number; limit?: number } = {},
  ): Promise<AxiosResponse<CustomerDetail>> {
    return api.get<CustomerDetail>(`/private/workspaces/${workspaceId}/customers/${customerId}`, {
      params: { page: opts.page ?? 1, limit: opts.limit ?? 25 },
    })
  },

  /**
   * A contact's recent sequence-trigger activity (Slice 4).
   * GET /workspaces/:id/customers/:customerId/trigger-activity
   */
  getCustomerTriggerActivity(
    workspaceId: string,
    customerId: number | string,
    limit = 20,
  ): Promise<AxiosResponse<{ activity: CustomerTriggerActivityRow[] }>> {
    return api.get(`/private/workspaces/${workspaceId}/customers/${customerId}/trigger-activity`, {
      params: { limit },
    })
  },

  /**
   * Update a customer's profile (whitelisted fields only).
   * PATCH /workspaces/:id/customers/:customerId
   */
  updateCustomer(
    workspaceId: string,
    customerId: number | string,
    data: UpdateCustomerData,
  ): Promise<AxiosResponse<Customer>> {
    return api.patch<Customer>(`/private/workspaces/${workspaceId}/customers/${customerId}`, data)
  },

  /**
   * Archive (soft-hide) a customer.
   * POST /workspaces/:id/customers/:customerId/archive
   */
  archiveCustomer(workspaceId: string, customerId: number | string): Promise<AxiosResponse<Customer>> {
    return api.post<Customer>(`/private/workspaces/${workspaceId}/customers/${customerId}/archive`)
  },

  /**
   * Restore an archived customer.
   * POST /workspaces/:id/customers/:customerId/unarchive
   */
  unarchiveCustomer(workspaceId: string, customerId: number | string): Promise<AxiosResponse<Customer>> {
    return api.post<Customer>(`/private/workspaces/${workspaceId}/customers/${customerId}/unarchive`)
  },

  /**
   * Get workspace leads
   * GET /workspaces/:id/leads
   */
  getLeads(id: string, page: number = 1, limit: number = 50): Promise<AxiosResponse<LeadsResponse>> {
    return api.get<LeadsResponse>(`/private/workspaces/${id}/leads`, { params: { page, limit } })
  },

  /**
   * Delete a lead
   * DELETE /workspaces/:id/leads/:leadId
   */
  deleteLead(id: string, leadId: number): Promise<AxiosResponse<{ success: boolean }>> {
    return api.delete<{ success: boolean }>(`/private/workspaces/${id}/leads/${leadId}`)
  },

  /**
   * Get workspace orders
   * GET /workspaces/:id/orders
   */
  getOrders(id: string): Promise<AxiosResponse<Order[]>> {
    return api.get<Order[]>(`/private/workspaces/${id}/orders`)
  },

  /**
   * Get workspace subscriptions (recurring plans)
   * GET /workspaces/:id/subscriptions
   */
  getSubscriptions(id: string): Promise<AxiosResponse<Subscription[]>> {
    return api.get<Subscription[]>(`/private/workspaces/${id}/subscriptions`)
  },

  /**
   * Get workspace transactions
   * GET /workspaces/:id/transactions
   */
  getTransactions(id: string): Promise<AxiosResponse<Transaction[]>> {
    return api.get<Transaction[]>(`/private/workspaces/${id}/transactions`)
  },

  /**
   * Get workspace tokens (credits remaining)
   * GET /workspaces/:id/tokens
   */
  getTokens(id: string): Promise<AxiosResponse<{ tokens: number }>> {
    return api.get<{ tokens: number }>(`/private/workspaces/${id}/tokens`)
  },

  /**
   * Remove a member from workspace
   * DELETE /workspaces/:workspaceId/members/:memberId
   */
  removeMember(workspaceId: string, memberId: string): Promise<AxiosResponse<{ message: string }>> {
    return api.delete<{ message: string }>(`/private/workspaces/${workspaceId}/members/${memberId}`)
  },

  /**
   * Update a member's role
   * PATCH /workspaces/:workspaceId/members/:memberId
   */
  updateMemberRole(workspaceId: string, memberId: string, role: string): Promise<AxiosResponse<{ message: string; role: string }>> {
    return api.patch<{ message: string; role: string }>(`/private/workspaces/${workspaceId}/members/${memberId}`, { role })
  },

  /**
   * Leave a workspace (non-owner only)
   * POST /workspaces/:workspaceId/leave
   */
  leaveWorkspace(workspaceId: string): Promise<AxiosResponse<{ message: string }>> {
    return api.post<{ message: string }>(`/private/workspaces/${workspaceId}/leave`)
  },

  /**
   * Get workspace branding data
   * GET /workspaces/get-branding-data/:workspaceId
   */
  getBranding(workspaceId: string): Promise<AxiosResponse<WorkspaceBranding>> {
    return api.get<WorkspaceBranding>(`/private/workspaces/get-branding-data/${workspaceId}`)
  },

  /**
   * Update workspace branding via workspace PATCH
   * PATCH /workspaces/:workspaceId with { branding: {...} }
   */
  updateBranding(workspaceId: string, branding: Partial<WorkspaceBranding>): Promise<AxiosResponse<Workspace>> {
    return api.patch<Workspace>(`/private/workspaces/${workspaceId}`, { branding })
  },

  /**
   * Get workspace tier and expiry
   * GET /workspaces/:workspaceId/tier
   */
  getTier(workspaceId: string): Promise<AxiosResponse<{ workspaceTier: string | null; expireAt: string | null }>> {
    return api.get<{ workspaceTier: string | null; expireAt: string | null }>(`/private/workspaces/${workspaceId}/tier`)
  },

  /**
   * Get available plans for a country
   * GET /workspaces/plans?country={country}
   */
  getPlans(country: string): Promise<AxiosResponse<any>> {
    return api.get('/private/workspaces/plans', { params: { country } })
  },

  /**
   * Upload workspace logo
   * POST /save-logo/:workspaceId (multipart form data)
   * variant: 'light' (default, colored-for-light-bg) | 'dark' (light-ink-for-dark-bg)
   */
  uploadLogo(
    workspaceId: string,
    file: File,
    variant: 'light' | 'dark' = 'light'
  ): Promise<AxiosResponse<{ file: { url: string; filename: string }; variant: string; message: string }>> {
    // Canonical logo upload lives in services/media/branding — delegate so
    // there's ONE implementation of the save-logo request across the app.
    return uploadWorkspaceLogo(workspaceId, file, variant)
  },

  /**
   * Get workspace-level default settings (images, analytics, search)
   * GET /private/workspaces/:workspaceId/defaults
   */
  getDefaults(workspaceId: string): Promise<AxiosResponse<WorkspaceDefaults>> {
    return api.get<WorkspaceDefaults>(`/private/workspaces/${workspaceId}/defaults`)
  },

  /**
   * Update workspace-level default settings (analytics, search)
   * PATCH /private/workspaces/:workspaceId/defaults
   */
  updateDefaults(workspaceId: string, data: Partial<WorkspaceDefaults> & { siteLockPassword?: string }): Promise<AxiosResponse<{ success: boolean; defaults: WorkspaceDefaults }>> {
    return api.patch(`/private/workspaces/${workspaceId}/defaults`, data)
  },

  /**
   * Send a dummy receipt email to preview it. Pass `receiptEmail` to test
   * unsaved on-screen settings; omit `to` to send to the logged-in user.
   * POST /private/workspaces/:workspaceId/receipt-email/test
   */
  sendTestReceiptEmail(
    workspaceId: string,
    data: { to?: string; receiptEmail?: Record<string, unknown> },
  ): Promise<AxiosResponse<{ success: boolean; to: string }>> {
    return api.post(`/private/workspaces/${workspaceId}/receipt-email/test`, data)
  },

  /**
   * Upload workspace-level default brand images (favicon, social, apple touch icon)
   * POST /private/workspaces/:workspaceId/default-images
   */
  uploadDefaultImages(workspaceId: string, formData: FormData): Promise<AxiosResponse<{ success: boolean; defaults: Partial<WorkspaceDefaults> }>> {
    return api.post(`/private/workspaces/${workspaceId}/default-images`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  },

  /**
   * Reset every page back to the workspace default search visibility.
   * Clears all per-page search-visibility overrides so pages follow the default again.
   * POST /private/workspaces/:workspaceId/apply-search-visibility
   */
  applySearchVisibility(workspaceId: string): Promise<AxiosResponse<{ success: boolean; updated: number }>> {
    return api.post(`/private/workspaces/${workspaceId}/apply-search-visibility`)
  },

  /**
   * Reset every page back to the workspace default title & description.
   * Clears all per-page title/description overrides so pages follow the default again.
   * POST /private/workspaces/:workspaceId/apply-title-desc
   */
  applyTitleDesc(workspaceId: string): Promise<AxiosResponse<{ success: boolean; updated: number }>> {
    return api.post(`/private/workspaces/${workspaceId}/apply-title-desc`)
  },

  /**
   * Reset every page back to the workspace default share image.
   * Clears all per-page share-image overrides so pages follow the default again.
   * POST /private/workspaces/:workspaceId/reset-page-share-images
   */
  resetPageShareImages(workspaceId: string): Promise<AxiosResponse<{ success: boolean; cleared: number }>> {
    return api.post(`/private/workspaces/${workspaceId}/reset-page-share-images`)
  },

  /**
   * Reset every page back to the workspace default analytics.
   * Clears all per-page tracking-script overrides so pages follow the default again.
   * POST /private/workspaces/:workspaceId/apply-analytics
   */
  applyAnalytics(workspaceId: string): Promise<AxiosResponse<{ success: boolean; updated: number }>> {
    return api.post(`/private/workspaces/${workspaceId}/apply-analytics`)
  },

  /**
   * Reset every page back to the workspace default e-commerce tracking.
   * Clears all per-page e-commerce overrides so pages follow the default again.
   * POST /private/workspaces/:workspaceId/reset-ecommerce-tracking
   */
  resetEcommerceTracking(workspaceId: string): Promise<AxiosResponse<{ success: boolean; updated: number }>> {
    return api.post(`/private/workspaces/${workspaceId}/reset-ecommerce-tracking`)
  },

  // ─── REDIRECTS ──────────────────────────────────────────────────

  /**
   * List all redirects for a workspace
   * GET /private/workspaces/:workspaceId/redirects
   */
  getRedirects(workspaceId: string): Promise<AxiosResponse<Redirect[]>> {
    return api.get<Redirect[]>(`/private/workspaces/${workspaceId}/redirects`)
  },

  /**
   * Create a new redirect
   * POST /private/workspaces/:workspaceId/redirects
   */
  createRedirect(workspaceId: string, data: { fromPath: string; toUrl: string; statusCode?: number; isActive?: boolean; forwardQueryString?: boolean }): Promise<AxiosResponse<Redirect>> {
    return api.post<Redirect>(`/private/workspaces/${workspaceId}/redirects`, data)
  },

  /**
   * Update a redirect
   * PATCH /private/redirects/:redirectId
   */
  updateRedirect(redirectId: number, data: Partial<{ fromPath: string; toUrl: string; statusCode: number; isActive: boolean; forwardQueryString: boolean }>): Promise<AxiosResponse<Redirect>> {
    return api.patch<Redirect>(`/private/redirects/${redirectId}`, data)
  },

  /**
   * Delete a redirect
   * DELETE /private/redirects/:redirectId
   */
  deleteRedirect(redirectId: number): Promise<AxiosResponse<{ success: boolean }>> {
    return api.delete(`/private/redirects/${redirectId}`)
  },

  bulkCreateRedirects(workspaceId: string, redirects: RedirectFields[]): Promise<AxiosResponse<RedirectBatchResult>> {
    return api.post(`/private/workspaces/${workspaceId}/redirects/bulk`, { redirects }, { timeout: 120000 })
  },

  bulkUpdateRedirects(workspaceId: string, updates: RedirectUpdate[]): Promise<AxiosResponse<RedirectBatchResult>> {
    return api.patch(`/private/workspaces/${workspaceId}/redirects/bulk`, { updates }, { timeout: 120000 })
  },

  bulkDeleteRedirects(workspaceId: string, ids: number[]): Promise<AxiosResponse<RedirectBatchResult>> {
    return api.delete(`/private/workspaces/${workspaceId}/redirects/bulk`, { data: { ids }, timeout: 120000 })
  },

  /**
   * Reorder redirects
   * PUT /private/workspaces/:workspaceId/redirects/reorder
   */
  reorderRedirects(workspaceId: string, orderedIds: number[]): Promise<AxiosResponse<Redirect[]>> {
    return api.put<Redirect[]>(`/private/workspaces/${workspaceId}/redirects/reorder`, { orderedIds })
  },

  // ─── FONTS ──────────────────────────────────────────────────────

  getFonts(): Promise<AxiosResponse<{ success: boolean; fonts: { family: string; category: string }[] }>> {
    return api.get('/private/fonts?all=true')
  },

  // ─── COLORS ──────────────────────────────────────────────────────

  getColors(workspaceId: string): Promise<AxiosResponse<any[]>> {
    return api.get(`/private/workspaces/${workspaceId}/colors`)
  },

  createColor(workspaceId: string, data: { name: string; value: string; roles?: string[] }): Promise<AxiosResponse<any>> {
    return api.post(`/private/workspaces/${workspaceId}/colors`, data)
  },

  updateColor(workspaceId: string, colorId: number, data: { name?: string; value?: string; roles?: string[] }): Promise<AxiosResponse<any>> {
    return api.put(`/private/workspaces/${workspaceId}/colors/${colorId}`, data)
  },

  deleteColor(workspaceId: string, colorId: number): Promise<AxiosResponse<{ success: boolean }>> {
    return api.delete(`/private/workspaces/${workspaceId}/colors/${colorId}`)
  },

  // ─── TYPOGRAPHY ──────────────────────────────────────────────────

  getTypography(workspaceId: string): Promise<AxiosResponse<any[]>> {
    return api.get(`/private/workspaces/${workspaceId}/typography`)
  },

  createTypography(workspaceId: string, data: Record<string, any>): Promise<AxiosResponse<any>> {
    return api.post(`/private/workspaces/${workspaceId}/typography`, data)
  },

  updateTypography(workspaceId: string, typographyId: number, data: Record<string, any>): Promise<AxiosResponse<any>> {
    return api.put(`/private/workspaces/${workspaceId}/typography/${typographyId}`, data)
  },

  deleteTypography(workspaceId: string, typographyId: number): Promise<AxiosResponse<{ success: boolean }>> {
    return api.delete(`/private/workspaces/${workspaceId}/typography/${typographyId}`)
  },

  regenerateBrandingCss(workspaceId: string): Promise<AxiosResponse<{ success: boolean; css: string }>> {
    return api.post(`/private/workspaces/${workspaceId}/branding/regenerate-css`)
  },

  // ─── RETIRE ──────────────────────────────────────────────────

  retire(workspaceId: string): Promise<AxiosResponse<{ message: string }>> {
    return api.post<{ message: string }>(`/private/workspaces/${workspaceId}/retire`)
  },

  // ─── CATALOG SYSTEM PAGES ─────────────────────────────────────
  //
  // Currently just /cart — opened in the regular page builder (cart is an
  // editable system page with a real component tree, unlike the synthetic
  // checkout / thank-you pages). 404 when workspace is single-product LP.
  getCatalogCartPage(workspaceId: string): Promise<AxiosResponse<{ id: number; name: string; slug: string; publishedAt: string | null; currentRevisionId: number | null }>> {
    return api.get(`/private/workspaces/${workspaceId}/catalog/system-pages/cart`)
  },
}
